import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import patientChatApi, {
  PatientChatCapabilitiesResponse,
  PatientChatMessageRequest,
  PatientChatMessageResponse,
  PatientChatSessionResponse,
  PatientChatSessionSaveRequest,
  PatientChatStreamHandlers,
} from "../lib/patientChat";

interface UsePatientChatCapabilitiesOptions {
  patientId?: number | null;
  enabled?: boolean;
}

interface SendPatientChatMessageParams {
  patientId: number;
  body: PatientChatMessageRequest;
  streamHandlers?: PatientChatStreamHandlers;
  signal?: AbortSignal;
}

interface UsePatientChatSessionOptions {
  patientId?: number | null;
  enabled?: boolean;
}

interface SavePatientChatSessionParams {
  patientId: number;
  body: PatientChatSessionSaveRequest;
}

export const patientChatKeys = {
  all: ["patientChat"] as const,
  capabilities: (patientId: number) => [...patientChatKeys.all, "capabilities", patientId] as const,
  session: (patientId: number) => [...patientChatKeys.all, "session", patientId] as const,
};

export function usePatientChatCapabilities({
  patientId,
  enabled = true,
}: UsePatientChatCapabilitiesOptions) {
  return useQuery<PatientChatCapabilitiesResponse>({
    queryKey:
      patientId && enabled
        ? patientChatKeys.capabilities(patientId)
        : [...patientChatKeys.all, "capabilities", "disabled"],
    queryFn: async () => {
      if (!patientId) {
        throw new Error("patientId is required");
      }

      return patientChatApi.getCapabilities(patientId);
    },
    enabled: Boolean(patientId) && enabled,
    staleTime: 30000,
  });
}

export function usePatientChatMessage() {
  return useMutation<PatientChatMessageResponse, Error, SendPatientChatMessageParams>({
    mutationFn: async ({ patientId, body, streamHandlers, signal }) =>
      patientChatApi.sendMessageStream(patientId, body, streamHandlers, signal),
  });
}

export function usePatientChatSession({ patientId, enabled = true }: UsePatientChatSessionOptions) {
  return useQuery<PatientChatSessionResponse>({
    queryKey:
      patientId && enabled
        ? patientChatKeys.session(patientId)
        : [...patientChatKeys.all, "session", "disabled"],
    queryFn: async () => {
      if (!patientId) {
        throw new Error("patientId is required");
      }

      return patientChatApi.getSession(patientId);
    },
    enabled: Boolean(patientId) && enabled,
    staleTime: 0,
    refetchOnMount: true,
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 2000 : false),
  });
}

export function useSavePatientChatSession() {
  return useMutation<PatientChatSessionResponse, Error, SavePatientChatSessionParams>({
    mutationFn: async ({ patientId, body }) => patientChatApi.saveSession(patientId, body),
  });
}

export function useClearPatientChatSession() {
  return useMutation<void, Error, number>({
    mutationFn: async (patientId) => patientChatApi.clearSession(patientId),
  });
}

interface PatientChatDraft {
  patientId: number;
  draftPrompt: string;
}

// Session writes (draft PUTs and the Clear History DELETE) run one at a time, so a
// slow request can never land after a newer one and overwrite it. A draft asked for
// while a write is in flight is queued and sent once that write settles, which is
// also how a draft saved on close gets its retry if the in-flight save fails. The
// PUT only stores draftPrompt and bumps the version on every write, so a draft the
// server already holds is skipped.
export function usePatientChatDraftSaver() {
  const { mutateAsync: saveSession } = useSavePatientChatSession();
  const { mutateAsync: clearSessionRequest } = useClearPatientChatSession();
  const savedDraftRef = useRef<PatientChatDraft | null>(null);
  const queuedDraftRef = useRef<PatientChatDraft | null>(null);
  const writeRef = useRef<Promise<void> | null>(null);
  const saveDraftRef = useRef<(draft: PatientChatDraft) => void>();
  // Highest session version a draft PUT from this hook has been confirmed at. A GET
  // answered before that write landed reports a lower version, which is how a reopen
  // can tell a stale session from a genuinely newer one. Versions only compare within
  // one session row: Clear History deletes the row and the next request recreates it
  // from version 0 under a new sessionId.
  const lastWrittenVersionRef = useRef<{
    patientId: number;
    sessionId: string;
    version: number;
  } | null>(null);
  // Exposed as state so a consumer waiting on it re-renders when the queue drains.
  const [hasPendingWrite, setHasPendingWrite] = useState(false);

  const runWrite = useCallback(<T>(write: () => Promise<T>) => {
    const request = Promise.resolve(writeRef.current).then(write);
    const settled: Promise<void> = request
      .catch(() => {})
      .then(() => {
        if (writeRef.current !== settled) return;
        writeRef.current = null;
        const queued = queuedDraftRef.current;
        queuedDraftRef.current = null;
        if (queued) saveDraftRef.current?.(queued);
        setHasPendingWrite(Boolean(writeRef.current));
      });
    writeRef.current = settled;
    setHasPendingWrite(true);
    return request;
  }, []);

  const saveDraft = useCallback(
    ({ patientId, draftPrompt }: PatientChatDraft) => {
      // Queue before the saved check: the write in flight may replace the saved draft,
      // so a draft that matches it now (say, a reverted edit) can still need sending.
      if (writeRef.current) {
        queuedDraftRef.current = { patientId, draftPrompt };
        return;
      }
      const saved = savedDraftRef.current;
      if (saved?.patientId === patientId && saved.draftPrompt === draftPrompt) return;
      runWrite(async () => {
        const session = await saveSession({ patientId, body: { draftPrompt } });
        // The server skips the write while a message is streaming and returns the
        // stored session instead, so only an echoed draft counts as saved.
        if ((session?.draftPrompt ?? "") === draftPrompt) {
          savedDraftRef.current = { patientId, draftPrompt };
          if (typeof session?.version === "number" && session.sessionId) {
            lastWrittenVersionRef.current = {
              patientId,
              sessionId: session.sessionId,
              version: session.version,
            };
          }
        }
      }).catch(() => {});
    },
    [runWrite, saveSession]
  );
  saveDraftRef.current = saveDraft;

  const markDraftSaved = useCallback((draft: PatientChatDraft) => {
    savedDraftRef.current = draft;
  }, []);

  const getLastWrittenVersion = useCallback(
    (patientId: number, sessionId: string | null | undefined): number | null => {
      const written = lastWrittenVersionRef.current;
      return written?.patientId === patientId && written.sessionId === sessionId
        ? written.version
        : null;
    },
    []
  );

  const clearSession = useCallback(
    (patientId: number) => {
      queuedDraftRef.current = null;
      return runWrite(async () => {
        await clearSessionRequest(patientId);
        savedDraftRef.current = { patientId, draftPrompt: "" };
        if (lastWrittenVersionRef.current?.patientId === patientId) {
          lastWrittenVersionRef.current = null;
        }
      });
    },
    [clearSessionRequest, runWrite]
  );

  return { saveDraft, markDraftSaved, clearSession, getLastWrittenVersion, hasPendingWrite };
}

export type PatientChatDraftSaver = ReturnType<typeof usePatientChatDraftSaver>;

export default usePatientChatMessage;
